import { DODA_SITE_URL } from '@doda/common/src/constants/urls';
import { createOpQuery } from 'components/SearchSideBar/logics/createQuery/details';
import { createJobOfferKindQuery } from 'components/SearchSideBar/logics/createQuery/details/jobOfferKind';
import { removeUndefinedFromObject } from '@doda/common/src/utils/objectUtil';
import { getKeys } from 'components/SearchSideBar/logics/tools';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { createOccupationUrlQuery } from 'components/SearchSideBar/logics/createQuery/occupationQuery';
import { createIndustryQuery } from 'components/SearchSideBar/logics/createQuery/industryQuery';
import { createIncomeQuery } from 'components/SearchSideBar/logics/createQuery/incomeQuery';
import { createKeywordQuery } from 'components/SearchSideBar/logics/createQuery/keywordQuery';
import { createConditionQuery } from 'logics/createConditionQuery';
import { DEFAULT_PARAMS } from '../../constants';
import { SearchConditionParams } from '../../interface';

/**
 * 選択された現在条件を元にクエリオブジェクトを生成
 *
 * @param {SelectedCurrentConditionType} selectedCurrentCondition - 選択された現在条件
 * @param {Partial<typeof DEFAULT_PARAMS>} defaultParams - デフォルトのパラメータ
 * @returns {SearchConditionParams} - 生成されたクエリオブジェクト
 */
export const createConditionQueryObject = (
  selectedCurrentCondition: SelectedCurrentConditionType,
  defaultParams: Partial<typeof DEFAULT_PARAMS>
): SearchConditionParams => {
  // 各条件のクエリを生成
  const occupationQuery = createOccupationUrlQuery(
    selectedCurrentCondition?.searchJobTypeLId ?? [],
    selectedCurrentCondition?.searchJobTypeMId ?? [],
    selectedCurrentCondition?.searchJobTypeSId ?? []
  );

  const areaQuery = createConditionQuery(
    selectedCurrentCondition?.areaId ?? [],
    'ar'
  );
  const prefectureQuery = createConditionQuery(
    selectedCurrentCondition?.prefectureId ?? [],
    'pr'
  );
  const cityQuery = createConditionQuery(
    selectedCurrentCondition?.cityId ?? [],
    'ci'
  );
  const wardQuery = createConditionQuery(
    selectedCurrentCondition?.wardId ?? [],
    'wa'
  );
  const routeQuery = createConditionQuery(
    selectedCurrentCondition?.routeId ?? [],
    'rt'
  );
  const stationQuery = createConditionQuery(
    selectedCurrentCondition?.stationId ?? [],
    'st'
  );
  const industryQuery = createIndustryQuery(
    selectedCurrentCondition?.industryLId ?? [],
    selectedCurrentCondition?.industrySId ?? []
  );
  const optionQuery = createOpQuery({
    employmentStatus: selectedCurrentCondition?.employmentStatus ?? [],
    isNewArrival: selectedCurrentCondition?.isNewArrival ?? false,
    isNearDeadline: selectedCurrentCondition?.isNearDeadline ?? false,
    holidayAndWayOfWorking:
      selectedCurrentCondition?.holidayAndWayOfWorking ?? [],
    requirements: selectedCurrentCondition?.requirements ?? [],
    workplaceEnvironment: selectedCurrentCondition?.workplaceEnvironment ?? [],
    welfareBenefits: selectedCurrentCondition?.welfareBenefits ?? [],
    languageSkill: selectedCurrentCondition?.languageSkills ?? [],
    positions: selectedCurrentCondition?.positions ?? [],
    employeeAverageAge: selectedCurrentCondition?.employeeAverageAge ?? [],
  });

  const incomeQuery = createIncomeQuery(
    selectedCurrentCondition?.lowerLimitIncome,
    selectedCurrentCondition?.upperLimitIncome
  );

  const includeKeywordQuery = createKeywordQuery(
    selectedCurrentCondition?.includedKeyword ?? [],
    selectedCurrentCondition?.includedKeywordCategory,
    true
  );

  const excludeKeywordQuery = createKeywordQuery(
    selectedCurrentCondition?.excludedKeyword ?? [],
    selectedCurrentCondition?.excludedKeywordCategory,
    false
  );

  const employNumQuery = createConditionQuery(
    selectedCurrentCondition?.employNum ?? [],
    'ne'
  );

  const establishQuery = createConditionQuery(
    selectedCurrentCondition?.establish ?? [],
    'es'
  );

  const jobOfferKindQuery = createJobOfferKindQuery(
    selectedCurrentCondition?.jobOfferKind,
    defaultParams?.tp
  );

  return {
    k: includeKeywordQuery.keyword,
    kwc: includeKeywordQuery.category,
    ni: excludeKeywordQuery.keyword,
    niwc: excludeKeywordQuery.category,
    ss: defaultParams?.ss ? `ss=${defaultParams.ss}` : undefined,
    op: optionQuery,
    pic: defaultParams?.pic ? `pic=${defaultParams.pic}` : undefined,
    ds: defaultParams?.ds ? `ds=${defaultParams.ds}` : undefined,
    oc: occupationQuery,
    ar: areaQuery,
    pr: prefectureQuery,
    ci: cityQuery,
    wa: wardQuery,
    rt: routeQuery,
    st: stationQuery,
    ind: industryQuery,
    ha: incomeQuery,
    ne: employNumQuery,
    es: establishQuery,
    so: defaultParams?.so ? `so=${defaultParams.so}` : undefined,
    tp: jobOfferKindQuery,
  };
};

/**
 * 生成したクエリのオブジェクトからクエリを生成
 *
 * @param {SearchConditionParams} queryObject - クエリのオブジェクト
 * @returns {string[]} - 生成されたクエリの配列
 */
export const createQueryUrl = (queryObject: SearchConditionParams) => {
  const params: string[] = [];

  const treatmentQueryObject = removeUndefinedFromObject(queryObject);

  getKeys(treatmentQueryObject).forEach((key) =>
    params.push(treatmentQueryObject[key])
  );

  return params;
};

/**
 * 生成したクエリなどを元に遷移先URLを生成
 *
 * @param {string[]} searchConditionQuery - 生成したクエリの配列
 * @returns {string} - 遷移先URL
 */
export const createUrl = (searchConditionQuery: string[]) => {
  const url = DODA_SITE_URL.jobSearchList.concat(
    '?',
    searchConditionQuery.join('&')
  );

  return url;
};
